// src/hooks/useDocumentMeta.ts
// Manages the browser tab title and favicon globally.
// - Restaurant owner logged in → restaurant name + logo
// - Public menu page → handled separately by MenuPage.tsx
// - Everyone else (not logged in, admin, etc.) → "Menu House" + platform logo

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { restaurantApi } from '../api';
import { UserRole } from '../types';
import { getImageUrl } from '../constants';

const PLATFORM_TITLE = 'Menu House';
const PLATFORM_FAVICON = '/hawas-logo.png';

/** Set the favicon <link> to a given URL */
const setFavicon = (url: string) => {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

export const useDocumentMeta = () => {
  const { user, isAuthenticated } = useAuth();

  const isRestaurantOwner =
    isAuthenticated && user?.role === UserRole.RESTAURANT_OWNER;

  // Fetch restaurant profile only for logged-in restaurant owners.
  // Reuses the same query key as useRestaurantProfile so data is shared.
  const { data: restaurant } = useQuery({
    queryKey: ['restaurant-profile'],
    queryFn: async () => {
      const response = await restaurantApi.getMyProfile();
      return response.data.data;
    },
    enabled: isRestaurantOwner,
    staleTime: 5 * 60 * 1000, // 5 min — avoid refetching just for the tab
  });

  useEffect(() => {
    if (isRestaurantOwner && restaurant) {
      // Logged-in restaurant owner → show restaurant name + logo
      document.title = restaurant.name || PLATFORM_TITLE;
      if (restaurant.logo) {
        setFavicon(getImageUrl(restaurant.logo));
      } else {
        setFavicon(PLATFORM_FAVICON);
      }
    } else {
      // Not logged in, or admin, or no restaurant data yet → platform defaults
      document.title = PLATFORM_TITLE;
      setFavicon(PLATFORM_FAVICON);
    }
  }, [isRestaurantOwner, restaurant]);
};
